import { useState } from "react";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { useGetFirmBillingSummaryQuery } from "../../../../store";

const formatTRYDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};

const formatTRY = (amount) => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatNumber = (n) => new Intl.NumberFormat("tr-TR").format(n ?? 0);

const BillRow = ({ bill }) => (
  <tr className="text-sm">
    <td className="py-1.5 pr-3 font-semibold text-gray-800 whitespace-nowrap">
      {bill.productTypeName || "-"}
    </td>
    <td className="py-1.5 px-3 text-gray-700 whitespace-nowrap">
      {/* Kota transferinde (bayi → müşteri) fiyat yok: yalnız miktar. */}
      {bill.unitPrice == null ? (
        <>{formatNumber(bill.quota)} kota</>
      ) : (
        <>
          {formatNumber(bill.quota)} <span className="text-gray-400">×</span> {formatTRY(bill.unitPrice)}
        </>
      )}
    </td>
    <td className="py-1.5 px-3 font-bold text-main whitespace-nowrap text-right">
      {formatTRY(bill.total)}
    </td>
    <td className="py-1.5 px-3 text-xs text-gray-500 whitespace-nowrap">
      {bill.billNo ? `#${bill.billNo}` : "-"}
    </td>
    <td className="py-1.5 pl-3 text-xs text-gray-500 whitespace-nowrap">
      {bill.billerName || "-"}
    </td>
  </tr>
);

// useSummaryQuery: rol grubuna göre doğru endpoint'e giden RTK Query hook'u
// (storage varsayılan; dealer InfoTab dealer hook'unu geçirir). Hook prop
// olarak koşulsuz çağrılır — rules-of-hooks korunur.
const FirmBillingSummaryCard = ({
  firmId,
  token,
  useSummaryQuery = useGetFirmBillingSummaryQuery,
}) => {
  const { data, isLoading, isError } = useSummaryQuery(
    { firmId, token },
    { skip: !firmId || !token }
  );
  const [expanded, setExpanded] = useState(false);

  const billing = data?.data;
  const bills = billing?.bills || [];
  const hasBills = bills.length > 0;
  const visibleBills = expanded ? bills : bills.slice(0, 3);
  const hiddenCount = bills.length - 3;

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-secondary/30">
            <FaFileInvoiceDollar className="w-4 h-4 text-main" />
          </div>
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide">
            Son Fatura
          </h3>
        </div>
        {billing?.lastBillDate && (
          <span className="text-xs sm:text-sm font-semibold text-gray-600">
            {formatTRYDate(billing.lastBillDate)}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
        </div>
      )}

      {!isLoading && isError && (
        <p className="text-sm text-fifth italic">Fatura bilgisi yüklenemedi</p>
      )}

      {!isLoading && !isError && !hasBills && (
        <p className="text-sm text-gray-400 italic">Henüz fatura kesilmedi</p>
      )}

      {!isLoading && !isError && hasBills && (
        <>
          <div className="overflow-x-auto overflow-y-auto max-h-64 -mx-1">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200">
                  <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide pr-3 pb-1.5">Ürün</th>
                  <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-3 pb-1.5">Hesap</th>
                  <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-3 pb-1.5">Toplam</th>
                  <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-3 pb-1.5">Fatura No</th>
                  <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide pl-3 pb-1.5">İşlemi Yapan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleBills.map((bill, idx) => (
                  <BillRow key={`${bill.billNo}-${idx}`} bill={bill} />
                ))}
              </tbody>
            </table>
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs font-semibold text-primary hover:text-main transition-colors"
            >
              {expanded ? "Daha az" : `+${hiddenCount} daha göster`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default FirmBillingSummaryCard;
